import { useState, useEffect, useRef } from 'react';
import { supabase } from '../supabaseClient';
import { fetchUserData, updateUsername, resetDailyCounts, initUserRow, fetchWordStats, updateUserStats } from '../services/supabaseService';
import { evaluateMissedDays, normalizeDate, shouldGrantMondayBonus, getFreezeCap, getYesterday } from '../services/streakService';
import { buyFreeze, canBuyFreeze, restoreStreak, canRestoreStreak } from '../services/coinService';
import { fetchAllWords } from '../services/wordService';

export const useAuth = ({ step, setStep }) => {
  const stepRef = useRef(step);
  const authRequestIdRef = useRef(0);
  useEffect(() => {
    stepRef.current = step;
  }, [step]);

  const today = normalizeDate(new Date());

  const [user, setUser] = useState(() => {
    try {
      const stored = localStorage.getItem("vocab_user");
      if (stored) {
        const parsed = JSON.parse(stored);


        const { updatedStreak, updatedFreezes } = evaluateMissedDays(parsed, today);

        let newGamesPlayed = parsed.gamesPlayedToday || 0;
        let newSuccessfulCount = parsed.successfulGamesCount || 0;

        if (parsed.lastPlayedDate !== today) {
          newGamesPlayed = 0;
          newSuccessfulCount = 0;
        }

        return {
          ...parsed,
          gamesPlayedToday: newGamesPlayed,
          successfulGamesCount: newSuccessfulCount,
          currentStreak: updatedStreak,
          streakFreezes: updatedFreezes,
          coins: parsed.coins || 0,
          lastCoinRewardDate: parsed.lastCoinRewardDate || null
        };
      }
    } catch (e) { }

    return {
      userId: null,
      username: "User",
      fullName: "User Name",
      gamesCompletedToday: 0,
      lastPlayedDate: null,
      currentStreak: 0,
      streakFreezes: 0,
      coins: 0,
      lastCoinRewardDate: null
    };
  });

  const [startupStreakStatus, setStartupStreakStatus] = useState(null);

  // Profile state
  const [isEditingUsername, setIsEditingUsername] = useState(false);
  const [tempUsername, setTempUsername] = useState("");
  const [profileStats, setProfileStats] = useState({
    practiced: 0,
    weak: 0,
    mastered: 0
  });
  const [weakWordsList, setWeakWordsList] = useState([]);
  const [masteredWordsList, setMasteredWordsList] = useState([]);
  const [practicedWordsList, setPracticedWordsList] = useState([]);
  const [showDictionary, setShowDictionary] = useState(false);
  const [showWeakWords, setShowWeakWords] = useState(false);
  const [showMasteredWords, setShowMasteredWords] = useState(false);
  const [showPracticedWords, setShowPracticedWords] = useState(false);
  const [dictionarySearch, setDictionarySearch] = useState("");

  // Persist user to localStorage
  useEffect(() => {
    localStorage.setItem("vocab_user", JSON.stringify(user));
  }, [user]);

  // SUPABASE AUTH SUBSCRIPTION
  useEffect(() => {
    const getInitialSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        handleAuthUser(session.user);
      } else {
        // Only redirect to onboarding if we're not already there
        if (stepRef.current !== "onboarding") {
          setStep("onboarding");
        }
      }
    };

    getInitialSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      // Only handle actual sign-in events, NOT token refreshes.
      if (_event === 'SIGNED_IN' || _event === 'INITIAL_SESSION') {
        if (session) {
          handleAuthUser(session.user);
        }
      } else if (_event === 'SIGNED_OUT') {
        setUser({
          id: null,
          userId: null,
          username: "User",
          fullName: "User Name",
          email: null,
          gamesCompletedToday: 0,
          successfulGamesCount: 0,
          lastPlayedDate: null,
          currentStreak: 0,
          streakFreezes: 0,
          coins: 0,
          lastCoinRewardDate: null
        });
        localStorage.removeItem("vocab_user");
        localStorage.removeItem("onboarding_state");
        localStorage.removeItem("onboarding_complete"); // Clearing this too for consistency
        setStep("onboarding");
      }
    });

    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAuthUser = async (sbUser) => {
    const requestId = ++authRequestIdRef.current;


    try {
      const { data: userData, error: fetchError } = await fetchUserData(sbUser.id);

      if (fetchError) {
        if (requestId !== authRequestIdRef.current) return;
        console.error("[AUTH] Error fetching from 'users' table:", fetchError);
        alert("Failed to load your profile. Please check your internet connection and refresh the page.");
        return; // Prevent overwriting local state with 0s if DB fetch fails
      }

      const googleName = sbUser.user_metadata?.full_name || "";
      const firstName = googleName.trim().split(/\s+/)[0] || "User";
      let finalUsername = userData?.username || firstName;
      let dailyCount = userData?.daily_games_count || 0;
      let successfulCount = userData?.successful_games_count || 0;
      let lastDate = userData?.last_active_date || null;

      // Expiry enforcement removed for free migration
      // ---------------------------------------------------------

      // Use Streak Service to evaluate freezes/missed days
      let { updatedStreak, updatedFreezes, freezeUsed, missedDays } = evaluateMissedDays(userData || {}, today);
      let lastMondayFreezeDate = userData?.last_monday_freeze_date || null;

      // Check for Monday Pro Bonus
      const isMondayBonusEligible = shouldGrantMondayBonus(userData || {}, today);
      if (isMondayBonusEligible) {
        const cap = getFreezeCap();
        if (updatedFreezes < cap) {
          updatedFreezes++;
          lastMondayFreezeDate = today;
        }
      }


      // DAILY RESET LOGIC
      if (lastDate && lastDate !== today && (dailyCount > 0 || successfulCount > 0)) {
        dailyCount = 0;
        successfulCount = 0;

        const { error: resetError } = await resetDailyCounts(sbUser.id);
        if (resetError) console.error("[AUTH] Error resetting daily count in DB:", resetError);
      }

      // If freeze was used, streak was broken, or Monday bonus granted, sync to DB
      const dbStreak = Number(userData?.streak) || 0;
      const dbFreezes = Number(userData?.streak_freezes) || 0;

      const streakBroken = dbStreak > 0 && updatedStreak === 0;
      const freezesChanged = dbFreezes !== updatedFreezes;

      // GUARD: Suspicious reset (Streak broken but freezes still remain)
      const isSuspicious = streakBroken && updatedFreezes > 0;

      const dbUpdates = { last_activity_at: new Date().toISOString() };

      if (!isSuspicious && (freezeUsed || streakBroken || freezesChanged || isMondayBonusEligible)) {
        dbUpdates.streak = updatedStreak;
        dbUpdates.streak_freezes = updatedFreezes;
        dbUpdates.last_monday_freeze_date = lastMondayFreezeDate;

        // HEALING LOGIC: If a freeze was used, move the last_active_date to yesterday
        if (freezeUsed) {
          lastDate = getYesterday(today);
          dbUpdates.last_active_date = lastDate;
        }

        await updateUserStats(sbUser.id, dbUpdates);
      } else {
        // Just update activity timestamp even if no streak changes
        await updateUserStats(sbUser.id, dbUpdates);
      }

      // STARTUP POPUP LOGIC
      const sessionShown = sessionStorage.getItem('startup_streak_shown');
      if (!sessionShown && !isSuspicious) {
        if (streakBroken) {
          setStartupStreakStatus({
            type: 'broken',
            previousStreak: dbStreak,
            currentStreak: 0,
            wasFreezeUsed: freezeUsed,
            missedDays: missedDays,
          });
          sessionStorage.setItem('startup_streak_shown', 'true');
        } else if (freezeUsed) {
          setStartupStreakStatus({
            type: 'protected',
            currentStreak: updatedStreak,
            freezesLeft: updatedFreezes,
          });
          sessionStorage.setItem('startup_streak_shown', 'true');
        }
      }

      // User Row Initialization (if needed)
      if (!userData) {
        const { data: initData, error: initError } = await initUserRow({
          id: sbUser.id,
          email: sbUser.email,
          username: finalUsername,
          streak: 0,
          daily_games_count: 0,
          last_active_date: null,
          streak_freezes: 0,
          last_freeze_earned_date: null,
        });

        if (initError) {
          console.error("[AUTH] initUserRow FAILED:", {
            message: initError.message,
            code: initError.code,
            details: initError.details,
            hint: initError.hint,
            full: initError
          });
        } else {
          // Success case - no log needed in production
        }
      }

      if (requestId !== authRequestIdRef.current) {
        return;
      }

      setUser(prev => ({
        ...prev,
        id: sbUser.id,
        username: finalUsername,
        fullName: sbUser.user_metadata?.full_name || finalUsername,
        email: sbUser.email,
        avatarUrl: sbUser.user_metadata?.avatar_url || null,
        currentStreak: updatedStreak,
        lastPlayedDate: lastDate,
        gamesCompletedToday: dailyCount,
        successfulGamesCount: successfulCount,
        streakFreezes: updatedFreezes,
        lastFreezeEarnedDate: userData?.last_freeze_earned_date || null,
        coins: userData?.coins || 0,
        lastCoinRewardDate: userData?.last_coin_reward_date || null,
        has_active_subscription: userData?.has_active_subscription === true,
        is_trialing: userData?.is_trialing === true,
        trial_end: userData?.trial_end || null,
        cancel_at_period_end: userData?.cancel_at_period_end === true,
        subscription_end: userData?.subscription_end || null,
        next_billing_date: userData?.next_billing_date || null,
        plan_type: userData?.plan_type || "monthly",
        onboarding_complete: userData?.onboarding_complete || false,
        onboarding_data: userData?.onboarding_data || null,
      }));

      // ---------- FREE ACCESS LOGIC ----------
      const isComplete = userData?.onboarding_complete === true;

      if (!isComplete) {
        setStep("onboarding");
      } else {
        setStep("home");
      }
      // ---------------------------------------

    } catch (err) {
      console.error("[AUTH] Unexpected error in profile initialization:", err);
      setUser(prev => ({ ...prev, id: sbUser.id, username: sbUser.user_metadata?.full_name || "User" }));
    }
  };


  const handleLogout = async () => {
    await supabase.auth.signOut();
  };

  const handleCancelSubscription = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const response = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/cancel-subscription`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Failed to cancel subscription');
      }

      return true;
    } catch (err) {
      console.error("[SUBSCRIPTION] Cancellation error:", err);
      throw err;
    }
  };

  const handleSaveUsername = async (usernameParam) => {
    const trimmed = (usernameParam || tempUsername).trim();

    if (trimmed.length < 2 || trimmed.length > 25) {
      alert("Username must be between 2 and 25 characters.");
      return;
    }

    try {
      const { data: { user: sbUser }, error: authError } = await supabase.auth.getUser();

      if (authError || !sbUser) {
        console.error("[PROFILE] Auth error or user not found:", authError);
        alert("Authentication failed. Please sign in again.");
        return;
      }

      const { data, error } = await updateUsername(sbUser.id, trimmed);

      if (error) {
        console.error("[PROFILE] Update failed:", error.message, error.details, error.hint);
        if (error.message.includes("policy") || error.code === "42501") {
          console.error("[PROFILE] RLS Policy rejection detected.");
          alert("Permission denied. Database RLS policy may be blocking the update.");
        } else {
          alert(`Unable to save username: ${error.message}`);
        }
        return;
      }

      setUser(prev => ({ ...prev, username: trimmed }));
      setIsEditingUsername(false);
    } catch (err) {
      console.error("[PROFILE] Unexpected fatal error during save:", err);
      alert("An unexpected error occurred. Please check the console for details.");
    }
  };

  const fetchProfileStats = async () => {
    try {
      const { data: { user: sbUser } } = await supabase.auth.getUser();
      if (!sbUser) return;

      const { data: stats } = await fetchWordStats(sbUser.id);
      if (!stats) return;

      const allWords = await fetchAllWords();
      const wordMap = new Map(allWords.map(w => [w.word.toLowerCase().trim(), w]));

      const practiced = stats.length;
      const mastered = stats.filter(s => (s.streak || 0) >= 4 && (s.seen_count || 0) >= 5).length;

      const weakWordObjects = stats
        .filter(s => (Number(s.streak) || 0) <= -2)
        .map(s => wordMap.get(s.word.toLowerCase().trim()))
        .filter(Boolean);

      const masteredWordObjects = stats
        .filter(s => (s.streak || 0) >= 4 && (s.seen_count || 0) >= 5)
        .map(s => wordMap.get(s.word.toLowerCase().trim()))
        .filter(Boolean);

      const practicedWordObjects = stats
        .map(s => wordMap.get(s.word.toLowerCase().trim()))
        .filter(Boolean);

      const weak = weakWordObjects.length;
      setProfileStats({ practiced, weak, mastered });
      setWeakWordsList(weakWordObjects);
      setMasteredWordsList(masteredWordObjects);
      setPracticedWordsList(practicedWordObjects);
    } catch (err) {
      console.error("Error fetching stats:", err);
    }
  };

  const handleOpenProfile = () => {
    setShowDictionary(false);
    setShowWeakWords(false);
    setShowMasteredWords(false);
    setShowPracticedWords(false);
    setDictionarySearch("");
    setIsEditingUsername(false);

    fetchProfileStats();
    setStep("profile");
  };

  const handleBuyFreeze = async () => {
    if (!canBuyFreeze(user)) {
      alert("Not enough coins or already at max freezes!");
      return;
    }

    const updates = buyFreeze(user);
    if (!updates) return;

    try {
      const { error } = await updateUserStats(user.id, updates);
      if (error) throw error;

      setUser(prev => ({
        ...prev,
        coins: updates.coins,
        streakFreezes: updates.streak_freezes
      }));
    } catch (err) {
      console.error("Error buying freeze:", err);
      alert("Failed to purchase freeze. Please try again.");
    }
  };

  const handleRestoreStreak = async (missedDays, previousStreak) => {
    const updates = restoreStreak(user, missedDays, previousStreak);
    if (!updates) return;

    try {
      const { error } = await updateUserStats(user.id, {
        streak: updates.streak,
        coins: updates.coins,
        last_active_date: getYesterday(normalizeDate())
      });
      if (error) throw error;

      setUser(prev => ({
        ...prev,
        coins: updates.coins,
        currentStreak: updates.streak,
        lastPlayedDate: getYesterday(normalizeDate())
      }));
      setStartupStreakStatus(null);
    } catch (err) {
      console.error("Error restoring streak:", err);
      alert("Failed to restore streak. Please try again.");
    }
  };

  const refreshUser = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (session) {
      await handleAuthUser(session.user);
    }
  };

  return {
    user,
    setUser,
    refreshUser,
    // Profile state
    isEditingUsername,
    setIsEditingUsername,
    tempUsername,
    setTempUsername,
    profileStats,
    weakWordsList,
    masteredWordsList,
    practicedWordsList,
    showDictionary,
    setShowDictionary,
    showWeakWords,
    setShowWeakWords,
    showMasteredWords,
    setShowMasteredWords,
    showPracticedWords,
    setShowPracticedWords,
    dictionarySearch,
    setDictionarySearch,
    // Actions
    handleOpenProfile,
    fetchProfileStats,
    startupStreakStatus,
    setStartupStreakStatus,
    handleBuyFreeze,
    canBuyFreeze,
    handleRestoreStreak,
    canRestoreStreak,
    handleSaveUsername,
    handleLogout,
    handleCancelSubscription
  };
};
